import { useId, useState } from 'react';

import { FONT_SIZES, MAX_FONT_SIZE, MIN_FONT_SIZE, parseFontSize } from './extensions';
import styles from './DocumentEditor.module.css';

/**
 * The font size in points: a number the person can type (6 - 144, half points allowed) or pick
 * from the usual sizes. Enter applies it; Escape or leaving the field cancels and shows the
 * current size again, so nothing changes by accident. `value` is the size of the selection, or
 * `'mixed'` when the selected text has several sizes (the field is then empty).
 */
export function FontSizeField({
  value,
  label,
  mixedLabel,
  invalidLabel,
  onApply,
}: {
  value: string;
  label: string;
  mixedLabel: string;
  invalidLabel: string;
  onApply: (pt: number) => void;
}) {
  const listId = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const mixed = value === 'mixed';
  const shown = draft ?? (mixed ? '' : value);

  const commit = (raw: string) => {
    const pt = parseFontSize(raw);
    if (pt === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setDraft(null);
    onApply(pt);
  };
  const cancel = () => {
    setInvalid(false);
    setDraft(null);
  };

  return (
    <>
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={false}
        aria-controls={listId}
        aria-label={label}
        aria-invalid={invalid || undefined}
        title={invalid ? invalidLabel : mixed ? mixedLabel : label}
        placeholder={mixed ? '—' : undefined}
        className={styles.sizeInput}
        data-testid="font-size"
        value={shown}
        list={listId}
        onFocus={(event) => {
          event.currentTarget.select();
        }}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          setInvalid(false);
          // a size chosen from the suggestions arrives as a replacement, not as typing
          const type = (event.nativeEvent as InputEvent).inputType;
          if (type === 'insertReplacementText') commit(next);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit(shown);
          } else if (event.key === 'Escape' && draft !== null) {
            event.preventDefault();
            event.stopPropagation();
            cancel();
          }
        }}
        onBlur={cancel}
      />
      <datalist id={listId}>
        {FONT_SIZES.map((size) => (
          <option key={size} value={String(size)} />
        ))}
      </datalist>
      <span className={styles.srOnly} role="status">
        {invalid ? `${invalidLabel} (${String(MIN_FONT_SIZE)}–${String(MAX_FONT_SIZE)})` : ''}
      </span>
    </>
  );
}
